import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  alpha,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import dayjs from 'dayjs';
import { LabelCaps } from '../LabelCaps';
import {
  changeBillingPeriod,
  previewBillingPeriodChange,
  type BillingPeriodChangePreview,
} from '../../../utils/subscriptionApi';
import type { BillingPeriod } from '../../../utils/paymentTiers';

interface SwitchBillingPeriodDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirmed: () => void;
  catalogAliId: string;
  from: BillingPeriod;
  to: BillingPeriod;
}

const PRICE: Record<BillingPeriod, number> = { monthly: 15, yearly: 120 };
const SHORT: Record<BillingPeriod, string> = { monthly: 'mo', yearly: 'yr' };

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export const SwitchBillingPeriodDialog: React.FC<SwitchBillingPeriodDialogProps> = ({
  open,
  onClose,
  onConfirmed,
  catalogAliId,
  from,
  to,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [preview, setPreview] = useState<BillingPeriodChangePreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    setError(null);
    setLoadingPreview(true);
    let cancelled = false;
    previewBillingPeriodChange(catalogAliId, to)
      .then((p) => {
        if (!cancelled) setPreview(p);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message || 'Could not preview the change.');
      })
      .finally(() => {
        if (!cancelled) setLoadingPreview(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, to, catalogAliId]);

  const toLabel = to === 'yearly' ? 'Yearly' : 'Monthly';

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await changeBillingPeriod(catalogAliId, to);
      onConfirmed();
      onClose();
    } catch (err) {
      setError((err as Error).message || 'Failed to change billing period.');
    } finally {
      setSubmitting(false);
    }
  };

  const newChargeCents = PRICE[to] * 100;
  const proratedCredit = preview ? newChargeCents - preview.proratedChargeCents : 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth fullScreen={fullScreen}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1.5 }}>
        {`Switch to ${toLabel} Billing`}
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 24px 1fr',
            alignItems: 'center',
            gap: 1.5,
            mb: 2.25,
          }}
        >
          <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, textAlign: 'center', opacity: 0.6 }}>
            <LabelCaps>From</LabelCaps>
            <Typography variant="h6" sx={{ mt: 0.75, mb: 0 }}>
              ${PRICE[from]}
              <Box component="span" sx={{ fontSize: 12, fontWeight: 400, fontFamily: theme.typography.fontFamily, color: 'text.secondary' }}>
                /{SHORT[from]}
              </Box>
            </Typography>
          </Box>
          <ArrowForwardIcon fontSize="small" sx={{ color: 'text.secondary', mx: 'auto' }} />
          <Box
            sx={{
              p: 1.5,
              border: '2px solid',
              borderColor: 'primary.main',
              borderRadius: 1,
              textAlign: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.04),
            }}
          >
            <LabelCaps sx={{ color: 'primary.main' }}>To</LabelCaps>
            <Typography variant="h6" sx={{ mt: 0.75, mb: 0 }}>
              ${PRICE[to]}
              <Box component="span" sx={{ fontSize: 12, fontWeight: 400, fontFamily: theme.typography.fontFamily, color: 'text.secondary' }}>
                /{SHORT[to]}
              </Box>
            </Typography>
          </Box>
        </Box>

        {to === 'yearly' && (
          <Alert icon={<SavingsOutlinedIcon />} severity="info" sx={{ mb: 2 }}>
            <strong>You&apos;ll save $60/year</strong> (2 months free) by switching to yearly billing.
          </Alert>
        )}

        {loadingPreview ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : preview ? (
          <Box sx={{ fontSize: 13, lineHeight: 1.7 }}>
            <Row label="Prorated credit from current plan" value={`−${formatCents(proratedCredit)}`} />
            <Row label={`New ${toLabel.toLowerCase()} charge`} value={`$${PRICE[to]}.00`} />
            <Divider sx={{ my: 1 }} />
            <Row label={<strong>Charged today</strong>} value={<strong>{formatCents(preview.proratedChargeCents)}</strong>} />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Next renewal: {dayjs(preview.nextRenewDate).format('MMM D, YYYY')}
            </Typography>
          </Box>
        ) : null}

        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={submitting || !preview}>
          Confirm Switch
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Row: React.FC<{ label: React.ReactNode; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
    <span>{label}</span>
    <span>{value}</span>
  </Box>
);
